import React from 'react';
import { motion } from 'framer-motion';
import { Link, useParams } from 'react-router-dom';
import { Button } from '../components/ui/Button';
import { H1, Text } from '../components/ui/Typography';
import { Card } from '../components/ui/Card';
import SafeIcon from '../common/SafeIcon';
import { translations } from '../translations';

const fadeIn = { hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } } };

const Home = () => {
  const { lang = 'nl' } = useParams();
  
  // Safe translation access with fallback to Dutch
  const rawT = translations[lang] || translations.nl;
  
  // Defensive structure mapping to prevent "undefined" crashes
  const t = {
    hero: rawT.hero || translations.nl.hero || {},
    trust: rawT.trust || translations.nl.trust || [],
    preview: rawT.preview || translations.nl.preview || {},
    steps: rawT.steps || translations.nl.steps || { items: [] },
    cta: rawT.cta || translations.nl.cta || {}
  };

  return (
    <div className="w-full">
      {/* Hero Section */}
      <section className="relative pt-12 pb-24 md:pt-16 md:pb-28 overflow-visible">
        <div className="absolute inset-0 overflow-hidden pointer-events-none -z-10">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-brand-50 rounded-full blur-3xl opacity-50"></div>
        </div>

        <div className="max-w-5xl mx-auto px-6 text-center overflow-visible">
          <motion.div initial="hidden" animate="visible" variants={fadeIn} className="space-y-6 md:space-y-8 overflow-visible">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-zinc-100 text-zinc-600 text-[10px] font-bold uppercase tracking-widest mb-2">
              <SafeIcon name="Shield" className="text-zinc-400" />
              <span>{t.hero.badge || ''}</span>
            </div>
            
            <div className="overflow-visible mb-8">
              <H1 className="text-gradient leading-[1.08] pb-[0.12em]">
                {(t.hero.title || '').split(',').map((part, i) => (
                  <React.Fragment key={i}>
                    {part}{i === 0 ? ',' : ''}
                    {i === 0 && <br className="hidden md:block" />}
                  </React.Fragment>
                ))}
              </H1>
            </div>

            <Text className="max-w-2xl mx-auto text-lg md:text-xl mt-2 md:mt-4">
              {t.hero.subtitle || ''}
            </Text>
            
            <div className="flex flex-col items-center gap-8 pt-4">
              <div className="flex flex-row items-center justify-center gap-4 w-full">
                <Link to={`/${lang}/scan`}>
                  <Button className="w-full sm:w-auto px-10 py-4 text-base shadow-xl">{t.hero.ctaPrimary || 'Start'}</Button>
                </Link>
                <Link to={`/${lang}/${lang === 'nl' ? 'hoe-het-werkt' : 'how-it-works'}`}>
                  <Button variant="secondary" className="w-full sm:w-auto px-10 py-4 text-base">{t.hero.ctaSecondary || 'Info'}</Button>
                </Link>
              </div>
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-[0.2em]">
                {t.hero.footer || ''}
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Trust Section */}
      <section className="py-10 bg-white border-y border-zinc-100">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-wrap justify-center gap-x-12 gap-y-4">
            {(t.trust || []).map((item, i) => (
              <div key={i} className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-widest text-navy-900/60">
                <SafeIcon name="Check" className="text-navy-900" />
                {item}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Preview Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <Card className="p-8 md:p-12 border-slate-100 bg-slate-50/30 overflow-hidden relative" hover={false}>
              <div className="absolute top-0 right-0 w-64 h-64 bg-navy-50 rounded-full blur-3xl -mr-32 -mt-32 opacity-60"></div>
              <div className="grid md:grid-cols-2 gap-12 items-center relative z-10">
                <div>
                  <h2 className="text-2xl md:text-3xl font-bold text-navy-900 mb-4">{t.preview.title || ''}</h2>
                  <p className="text-slate-600 mb-8 leading-relaxed">
                    {t.preview.desc || ''}
                  </p>
                  <ul className="space-y-4 mb-8">
                    {(t.preview.points || []).map((point, i) => (
                      <li key={i} className="flex items-center gap-3 text-sm font-semibold text-navy-900">
                        <div className="w-5 h-5 rounded-full bg-navy-900 flex items-center justify-center text-white">
                          <SafeIcon name="Check" className="text-[10px]" />
                        </div>
                        {point}
                      </li>
                    ))}
                  </ul>
                  <p className="text-xs font-medium text-slate-400 italic">
                    {t.preview.footer || ''}
                  </p>
                </div>
                <div className="relative hidden md:block">
                  <div className="bg-white rounded-2xl shadow-premium p-6 border border-slate-100 transform rotate-2">
                    <div className="h-2 w-12 bg-navy-900 rounded mb-4"></div>
                    <div className="space-y-3">
                      <div className="h-1.5 w-full bg-slate-100 rounded"></div>
                      <div className="h-1.5 w-5/6 bg-slate-100 rounded"></div>
                    </div>
                    <div className="mt-8 flex justify-between items-end">
                      <div className="w-12 h-12 rounded-full border-4 border-slate-50 flex items-center justify-center text-navy-900 font-bold">8.4</div>
                      <div className="h-6 w-16 bg-slate-50 rounded"></div>
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* Steps Section */}
      <section className="py-32 bg-zinc-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-20">
            <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-navy-900 mb-4 font-sans">{t.steps.title || ''}</h2>
            <Text>{t.steps.subtitle || ''}</Text>
          </div>
          <div className="grid md:grid-cols-3 gap-8 relative">
            {(t.steps.items || []).map((step, idx) => (
              <Card key={idx} className="p-8 relative z-10 bg-white/80 backdrop-blur-sm border-slate-100">
                <div className="w-12 h-12 bg-zinc-100 rounded-2xl flex items-center justify-center mb-6 text-navy-900">
                  <SafeIcon name={step.icon || 'Shield'} className="text-xl" />
                </div>
                <h3 className="text-xl font-semibold mb-3 text-navy-900">{step.title || ''}</h3>
                <Text subtle className="text-sm">{step.desc || ''}</Text>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-32 bg-navy-950 text-white text-center rounded-t-[3rem] relative overflow-hidden">
        <div className="absolute inset-0 bg-blueprint-pattern opacity-[0.03]"></div>
        <div className="max-w-3xl mx-auto px-6 relative z-10">
          <h2 className="text-4xl md:text-5xl font-semibold mb-6 tracking-tight">{t.cta.title || ''}</h2>
          <p className="text-slate-400 text-lg mb-10">{t.cta.desc || ''}</p>
          <div className="flex flex-col items-center gap-6">
            <Link to={`/${lang}/scan`}>
              <Button variant="white" className="px-12 py-5 text-base">{t.cta.button || 'Start'}</Button>
            </Link>
            <div className="flex gap-6 opacity-50">
              <span className="text-[10px] uppercase font-bold tracking-widest flex items-center gap-2"> <SafeIcon name="Lock" /> {t.cta.security || 'SECURE'} </span>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;